import { useMemo, useState } from 'react';
import { minToTime } from './calendarLayout';
import { calType, typeVars, type CalSession } from './calendarTypes';
import type { RosterStaff } from './todayRoster';

/* The side card: what is happening now, what is about to, and what needs a
 * person to do something about it.
 *
 * Everything here is derived from the same day query the grid draws, so the
 * two can never disagree. Nothing in this file writes — the actions call back
 * into the day sheet, which owns the fetches. */

type AlertKind = 'unassigned' | 'waitlist' | 'lowfill';

interface Alert {
  kind: AlertKind;
  s: CalSession;
  title: string;
  detail: string;
  action: string;
}

const inMin = (m: number): string =>
  m < 60 ? `in ${m} min` : `in ${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ''}`;

export function TodaySide({ sessions, isToday, now, onPick, onAssign, onAddSpot, onPromote, freeStaffFor }: {
  sessions: CalSession[];
  isToday: boolean;
  /** Gym-local minutes past midnight. */
  now: number;
  onPick: (s: CalSession) => void;
  onAssign: (s: CalSession, staffId: string) => void;
  onAddSpot: (s: CalSession) => void;
  onPromote: (s: CalSession) => void;
  /** On-shift providers with nothing else booked over this session. */
  freeStaffFor: (s: CalSession) => RosterStaff[];
}) {
  /** Which "Assign" row has its staff picker open. */
  const [picking, setPicking] = useState<string | null>(null);

  const open = useMemo(() => sessions.filter(s => s.status !== 'cancelled'), [sessions]);

  /* A court that is bookable right now is not "running" — nobody is standing in
     front of a class. Slots are counted in one line under the cards instead. */
  const live = isToday ? open.filter(s => !s.slot && s.start <= now && s.end > now) : [];
  const liveSlots = isToday ? open.filter(s => s.slot && s.start <= now && s.end > now) : [];

  const next = useMemo(() => {
    const from = isToday ? now : -1;
    const to = isToday ? now + 120 : 24 * 60;
    return open
      .filter(s => s.start > from && s.start < to)
      .sort((a, b) => a.start - b.start)
      .slice(0, 7);
  }, [open, isToday, now]);

  const alerts = useMemo<Alert[]>(() => {
    const future = open.filter(s => !isToday || s.start > now);
    const at = (s: CalSession) => `${s.name} · ${minToTime(s.start)}`;
    return [
      ...future
        .filter(s => s.staffed && !s.who)
        .map<Alert>(s => ({ kind: 'unassigned', s, title: at(s), detail: 'No coach assigned', action: 'Assign' })),
      ...future
        .filter(s => s.wait > 0)
        .map<Alert>(s => ({ kind: 'waitlist', s, title: at(s), detail: `Full · ${s.wait} on waitlist`, action: 'Add spot' })),
      ...future
        .filter(s => s.cap > 2 && !s.slot && s.booked / s.cap < 0.35)
        .map<Alert>(s => ({ kind: 'lowfill', s, title: at(s), detail: `${s.booked}/${s.cap} booked`, action: 'Promote' })),
    ].slice(0, 6);
  }, [open, isToday, now]);

  return (
    <>
      {isToday && (
        <div className="a-sd-sec">
          <h4><span className="hd">Live now</span><span className="cnt">{minToTime(now)}</span></h4>
          {live.map(s => {
            const T = calType(s.type);
            const elapsed = Math.min(1, Math.max(0, (now - s.start) / Math.max(1, s.end - s.start)));
            return (
              <button key={s.id} className="a-lv-card" style={typeVars(T)} onClick={() => onPick(s)}>
                <span className="lc-t"><b className="trunc">{s.name}</b><span>ends {minToTime(s.end)}</span></span>
                {/* A PT hour's resource *is* the trainer, so don't say the
                    name twice. */}
                <span className="lc-m trunc">{[...new Set([s.res, s.who].filter(Boolean))].join(' · ')}</span>
                <span className="lc-p" aria-hidden="true"><span style={{ width: `${elapsed * 100}%` }} /></span>
                <span className="lc-m">
                  {s.cap === 1
                    ? s.member || (s.booked ? 'Booked' : 'Open')
                    : <><b>{s.checkedIn}</b>/{s.booked} checked in</>}
                </span>
              </button>
            );
          })}
          {!live.length && <p className="a-sd-empty">Nothing running right now.</p>}
          {liveSlots.length > 0 && (
            <p className="a-sd-note">
              + {liveSlots.length} bookable slot{liveSlots.length === 1 ? '' : 's'} in use ·{' '}
              {liveSlots.reduce((a, s) => a + s.booked, 0)} members
            </p>
          )}
        </div>
      )}

      <div className="a-sd-sec">
        <h4><span className="hd">{isToday ? 'Up next · 2 h' : 'First up'}</span></h4>
        {next.map((s, i) => (
          <button key={s.id} className="a-nx" onClick={() => onPick(s)}>
            <i style={{ background: calType(s.type).c }} aria-hidden="true" />
            <span className="t">{minToTime(s.start)}</span>
            <span className="n">
              <span className="trunc">{s.slot ? `${s.name} · ${s.res}` : s.name}</span>
              <em>
                {i === 0 && isToday ? `${inMin(s.start - now)} · ` : ''}
                {s.type === 'pt' ? s.member || 'Open' : s.who || s.res}
              </em>
            </span>
            <span className="f">{s.cap === 1 ? (s.booked ? 'Booked' : 'Open') : `${s.booked}/${s.cap}`}</span>
          </button>
        ))}
        {!next.length && (
          <p className="a-sd-empty">
            {isToday ? 'Nothing else in the next 2 hours.' : 'Nothing scheduled this day.'}
          </p>
        )}
      </div>

      <div className="a-sd-sec">
        <h4><span className="hd">Needs attention</span><span className="cnt">{alerts.length}</span></h4>
        {alerts.map(a => {
          const free = a.kind === 'unassigned' && picking === a.s.id ? freeStaffFor(a.s) : [];
          return (
            <div key={`${a.kind}-${a.s.id}`} className={`a-al a-al-${a.kind}`}>
              <div className="a-al-txt">
                <button className="a-al-open trunc" onClick={() => onPick(a.s)}>{a.title}</button>
                <em>{a.detail}</em>
                {picking === a.s.id && (
                  <div className="a-al-pick">
                    {free.length
                      ? free.map(st => (
                          <button key={st.id} onClick={() => { setPicking(null); onAssign(a.s, st.id); }}>
                            {st.name}
                          </button>
                        ))
                      : <p>Nobody on shift is free then.</p>}
                  </div>
                )}
              </div>
              <button
                className="a-btn sm"
                onClick={() => {
                  if (a.kind === 'unassigned') setPicking(p => (p === a.s.id ? null : a.s.id));
                  else if (a.kind === 'waitlist') onAddSpot(a.s);
                  else onPromote(a.s);
                }}
              >
                {a.action}
              </button>
            </div>
          );
        })}
        {!alerts.length && (
          <p className="a-sd-empty">
            {isToday ? 'All clear for the rest of the day.' : 'All clear for this day.'}
          </p>
        )}
      </div>
    </>
  );
}
